import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { LanguageProvider } from "@/contexts/LanguageContext";
import LandingPage from "./pages/LandingPage";

const HomePage = lazy(() => import("./pages/HomePage"));
const ClinicalProblemPage = lazy(() => import("./pages/ClinicalProblemPage"));
const ScientificFoundationsPage = lazy(() => import("./pages/ScientificFoundationsPage"));
const ClinicalEvidencePage = lazy(() => import("./pages/ClinicalEvidencePage"));
const ApplicationsPage = lazy(() => import("./pages/ApplicationsPage"));
const TechnologyPlatformPage = lazy(() => import("./pages/TechnologyPlatformPage"));
const ResearchDevelopmentPage = lazy(() => import("./pages/ResearchDevelopmentPage"));
const ScientificLibraryPage = lazy(() => import("./pages/ScientificLibraryPage"));
const ClinicalCollaborationsPage = lazy(() => import("./pages/ClinicalCollaborationsPage"));
const ClinicalProDemoPage = lazy(() => import("./pages/ClinicalProDemoPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

function AnimatedRoutes() {
  const location = useLocation();

  return (
    <main>
      <Suspense fallback={null}>
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<LandingPage />} />
            <Route path="/home" element={<HomePage />} />
            <Route path="/clinical-problem" element={<ClinicalProblemPage />} />
            <Route path="/scientific-foundations" element={<ScientificFoundationsPage />} />
            <Route path="/clinical-evidence" element={<ClinicalEvidencePage />} />
            <Route path="/applications" element={<ApplicationsPage />} />
            <Route path="/technology-platform" element={<TechnologyPlatformPage />} />
            <Route path="/research-development" element={<ResearchDevelopmentPage />} />
            <Route path="/scientific-library" element={<ScientificLibraryPage />} />
            <Route path="/clinical-collaborations" element={<ClinicalCollaborationsPage />} />
            <Route path="/clinical-pro-demo" element={<ClinicalProDemoPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AnimatePresence>
      </Suspense>
    </main>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AnimatedRoutes />
        </BrowserRouter>
      </TooltipProvider>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;
